import { computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

export interface AuthPermissions {
  canViewRoles: boolean
  canEditRoles: boolean
  canDeleteRoles: boolean
  canViewUsers: boolean
  canEditUsers: boolean
  canDeleteUsers: boolean
  canManageUsers: boolean
  canViewLogs: boolean
  canCreateLogs: boolean
  canEditLogs: boolean
  canDeleteLogs: boolean
  isAdmin: boolean
  isTurnCareAdmin: boolean
  isCustomerAdmin: boolean
}

/**
 * Derive permission flags from the user's Cognito groups.
 *
 * TurnCareAdmin is the platform (super) admin; CustomerAdmin is a tenant admin.
 * `isAdmin` is the union of both tiers. Role editing/deletion is restricted to
 * TurnCareAdmin, so customer admins get a read-only Roles page.
 *
 * Pure function (no store/router access) so it can be unit-tested directly.
 */
export function computePermissions(userGroups: string[]): AuthPermissions {
  const isTurnCareAdmin = userGroups.includes('TurnCareAdmin')
  const isCustomerAdmin = userGroups.includes('CustomerAdmin')
  const isAdmin = isTurnCareAdmin || isCustomerAdmin

  return {
    canViewRoles: isAdmin || userGroups.includes('roles_viewer'),
    canEditRoles: isTurnCareAdmin || userGroups.includes('roles_editor'),
    canDeleteRoles: isTurnCareAdmin,
    canViewUsers: isAdmin || userGroups.includes('user_manager') || userGroups.includes('users_viewer'),
    canEditUsers: isAdmin || userGroups.includes('user_manager') || userGroups.includes('users_editor'),
    canDeleteUsers: isAdmin,
    canManageUsers: isAdmin || userGroups.includes('user_manager'),
    canViewLogs: isAdmin || userGroups.includes('auditors') || userGroups.includes('managers'),
    canCreateLogs: isAdmin || userGroups.includes('managers'),
    canEditLogs: isAdmin,
    canDeleteLogs: isAdmin,
    isAdmin,
    isTurnCareAdmin,
    isCustomerAdmin,
  }
}

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()

  const permissions = computed<AuthPermissions>(() => computePermissions(authStore.userGroups))

  watch(
    () => authStore.isAuthenticated,
    (isAuthenticated) => {
      if (!isAuthenticated && router.currentRoute.value.meta.requiresAuth) {
        router.push('/login')
      }
    }
  )

  return {
    user: computed(() => authStore.user),
    isAuthenticated: computed(() => authStore.isAuthenticated),
    userGroups: computed(() => authStore.userGroups),
    userName: computed(() => authStore.userName),
    loading: computed(() => authStore.loading),
    error: computed(() => authStore.error),
    permissions,
    logout: authStore.logout,
    fetchUser: authStore.fetchUser
  }
}
